/* sugar:assets/okendo-reviews.js@2.2.0 */
/*
  Okendo Style Reviews — the clicks behind sections/okendo-reviews.liquid.

  The server already draws the list in its default order, hides everything past the first page,
  writes the relative dates and clamps long reviews, so nothing here moves anything on load. This
  file adds what needs a click: filters, sort, Show More, Read More, helpful votes, the photo
  lightbox and the review form.

  Saving a vote or a review needs a backend, which a theme does not have. Every such call goes
  through window.SugarReviews.adapter, whose built-in methods answer on the page, and every action
  is also announced as a bubbling sugar:reviews:* event. A store connects its review app by
  replacing the adapter (catalog/guides/sections/okendo-reviews.md, "Connecting a reviews backend"):

    window.SugarReviews.adapter = {
      remote: true,                          // optional: filters, sort and Show More ask load()
      vote: function (detail) { … },          // → Promise
      submit: function (formData, detail) { … },
      ask: function (formData, detail) { … }, // a question from the Questions tab → Promise
      load: function (params) { … }           // → Promise<{ reviews, total, hasMore } | null>
    };

  Question blocks add Reviews | Questions tabs. The questions are drawn on the server like the
  reviews (first page shown, the rest hidden); this file switches tabs, sorts them by date,
  shows more and sends the Ask a Question form.
*/
(function () {
  'use strict';

  if (!window.customElements || customElements.get('sugar-reviews')) return;

  var SR = (window.SugarReviews = window.SugarReviews || {});

  /* The built-in adapter: a vote and a review are accepted on the page, and load() has nothing
     beyond the reviews already in the section. Any method the store's adapter leaves out falls
     back to these. */
  var LOCAL = {
    vote: function () {
      return Promise.resolve({ ok: true });
    },
    submit: function () {
      return new Promise(function (resolve) {
        setTimeout(function () {
          resolve({ ok: true });
        }, 600);
      });
    },
    load: function () {
      return Promise.resolve(null);
    },
    ask: function () {
      return new Promise(function (resolve) {
        setTimeout(function () {
          resolve({ ok: true });
        }, 600);
      });
    },
  };
  SR.localAdapter = LOCAL;
  if (!SR.adapter) SR.adapter = {};

  function call(name, args) {
    var adapter = SR.adapter || {};
    var fn = typeof adapter[name] === 'function' ? adapter[name] : LOCAL[name];
    try {
      return Promise.resolve(fn.apply(adapter, args));
    } catch (err) {
      return Promise.reject(err);
    }
  }

  function remote() {
    return !!(SR.adapter && SR.adapter.remote);
  }

  function storeGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function storeSet(key, value) {
    try {
      if (value) window.localStorage.setItem(key, value);
      else window.localStorage.removeItem(key);
    } catch (e) {}
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function norm(value) {
    return String(value || '').trim().toLowerCase();
  }

  /* A detail value can hold several answers ("Wet Food, Dry Kibble"); each one is a filter chip. */
  function splitValues(value) {
    if (!value) return [];
    return String(value)
      .split(',')
      .map(function (v) {
        return v.trim();
      })
      .filter(Boolean);
  }

  /* Filter by details: one label per line, optionally followed by | and its chips in order. A line
     with no | may hold several labels separated by commas. */
  function parseGroups(raw) {
    var out = [];
    String(raw || '')
      .split(/\r?\n/)
      .forEach(function (line) {
        line = line.trim();
        if (!line) return;
        var bar = line.indexOf('|');
        if (bar !== -1) {
          var label = line.slice(0, bar).trim();
          if (label) out.push({ label: label, values: splitValues(line.slice(bar + 1)) });
          return;
        }
        line.split(',').forEach(function (label) {
          label = label.trim();
          if (label) out.push({ label: label, values: null });
        });
      });
    return out;
  }

  /* Keywords: one per line, optionally followed by | and the words it finds. Without them it finds
     its own words of four letters or more ("Coat & Skin Health" finds coat, skin and health). */
  function parseKeywords(raw) {
    var out = [];
    String(raw || '')
      .split(/\r?\n/)
      .forEach(function (line) {
        line = line.trim();
        if (!line) return;
        var bar = line.indexOf('|');
        var label = (bar === -1 ? line : line.slice(0, bar)).trim();
        if (!label) return;
        var terms = bar === -1 ? [] : splitValues(line.slice(bar + 1));
        if (!terms.length) {
          terms = norm(label)
            .split(/[^0-9a-zÀ-￿]+/)
            .filter(function (w) {
              return w.length >= 4;
            });
          if (!terms.length) terms = [label];
        }
        out.push({ label: label, finds: terms.map(wordFinder) });
      });
    return out;
  }

  /* Finds a word, or the start of one, in lower-case text: "skin" finds "skin" and "skins", never
     the "skin" inside "askin". */
  function wordFinder(term) {
    var t = norm(term).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    var re;
    try {
      re = new RegExp('(?:^|[^\\p{L}\\p{N}])' + t, 'u');
    } catch (e) {
      re = new RegExp('(?:^|[^0-9a-z\\u00c0-\\uffff])' + t);
    }
    return function (text) {
      return re.test(text);
    };
  }

  function parseDate(value) {
    var m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(value || '');
    if (!m) return null;
    var d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    return isNaN(d.getTime()) ? null : d;
  }

  /* The same steps the section's Liquid writes on the server, so a refresh here only changes the
     text when a cached page has aged past a boundary. */
  function relativeDate(value) {
    var d = parseDate(value);
    if (!d) return '';
    var diffMs = Date.now() - d.getTime();
    var hours = Math.floor(diffMs / 3600000);
    var days = Math.floor(diffMs / 86400000);
    var n;
    if (diffMs < 3600000) return 'just now';
    if (hours < 24) return hours + (hours === 1 ? ' hour ago' : ' hours ago');
    if (days < 7) return days + (days === 1 ? ' day ago' : ' days ago');
    if (days < 30) {
      n = Math.floor(days / 7);
      return n + (n === 1 ? ' week ago' : ' weeks ago');
    }
    if (days < 365) {
      n = Math.floor(days / 30);
      return n + (n === 1 ? ' month ago' : ' months ago');
    }
    n = Math.floor(days / 365);
    return n + (n === 1 ? ' year ago' : ' years ago');
  }

  /* One row's data, read once from its attributes. Helpful counts are the merchant's, before this
     visitor's vote, so a vote never reorders the list under the pointer. */
  function rowData(row) {
    if (row._rv) return row._rv;
    var details = {};
    try {
      details = JSON.parse(row.getAttribute('data-details') || '{}') || {};
    } catch (e) {}
    var lookup = {};
    Object.keys(details).forEach(function (label) {
      lookup[norm(label)] = details[label];
    });
    var date = parseDate(row.getAttribute('data-date'));
    var words = ['.sugar-okendo-reviews__title', '[data-rv-text]', '.sugar-okendo-reviews__name']
      .map(function (sel) {
        var el = row.querySelector(sel);
        return el ? el.textContent : '';
      })
      .join(' ');
    row._rv = {
      text: norm(words.replace(/\s+/g, ' ')),
      id: row.getAttribute('data-review-id'),
      index: parseInt(row.getAttribute('data-index'), 10) || 0,
      rating: parseInt(row.getAttribute('data-rating'), 10) || 0,
      time: date ? date.getTime() : null,
      up: parseInt(row.getAttribute('data-helpful-up'), 10) || 0,
      down: parseInt(row.getAttribute('data-helpful-down'), 10) || 0,
      photos: row.getAttribute('data-has-photos') === 'true',
      details: details,
      lookup: lookup,
    };
    return row._rv;
  }

  function byIndex(a, b) {
    return a.index - b.index;
  }

  /* Reviews without a date sort last both ways. */
  var SORTS = {
    most_helpful: function (a, b) {
      return b.up - a.up || byIndex(a, b);
    },
    least_helpful: function (a, b) {
      return a.up - b.up || byIndex(a, b);
    },
    most_recent: function (a, b) {
      if (a.time === null || b.time === null) return (a.time === null) - (b.time === null) || byIndex(a, b);
      return b.time - a.time || byIndex(a, b);
    },
    oldest: function (a, b) {
      if (a.time === null || b.time === null) return (a.time === null) - (b.time === null) || byIndex(a, b);
      return a.time - b.time || byIndex(a, b);
    },
    photos: function (a, b) {
      return (b.photos ? 1 : 0) - (a.photos ? 1 : 0) || byIndex(a, b);
    },
    highest: function (a, b) {
      return b.rating - a.rating || byIndex(a, b);
    },
    lowest: function (a, b) {
      return a.rating - b.rating || byIndex(a, b);
    },
  };

  function starsMarkup(count) {
    var out = '';
    for (var i = 0; i < count; i++) out += '<span class="sugar-okendo-reviews__star" aria-hidden="true"></span>';
    return out;
  }

  class SugarReviews extends HTMLElement {
    connectedCallback() {
      if (this._started) return;
      this._started = true;

      this.sectionId = this.getAttribute('data-section-id') || '';
      this.perPage = parseInt(this.getAttribute('data-per-page'), 10) || 5;
      this.sort = this.getAttribute('data-sort') || 'most_helpful';
      this.total = parseInt(this.getAttribute('data-total'), 10) || 0;
      this.countLabel = this.getAttribute('data-count-label') || '[count]';
      this.compact = this.hasAttribute('data-compact');
      this.groups = parseGroups(this.getAttribute('data-filter-groups'));
      this.keywords = parseKeywords(this.getAttribute('data-keywords'));
      this.search = this.hasAttribute('data-search');
      this.filters = { rating: [], keywords: [], groups: {}, query: '' };
      this.shown = this.perPage;
      this.remoteHasMore = null;
      this.remoteTotal = null;

      this.list = this.querySelector('[data-rv-list]');
      this.moreWrap = this.querySelector('[data-rv-more-wrap]');
      this.countEl = this.querySelector('[data-rv-count]');

      this.addEventListener('click', this._onClick.bind(this));
      this.addEventListener('change', this._onChange.bind(this));
      this.addEventListener('submit', this._onSubmit.bind(this));
      this.addEventListener('input', this._onInput.bind(this));
      this.addEventListener('keydown', this._onKey.bind(this));

      this._restoreVotes();
      this._refreshDates();
      this._setupStrip();
      this._setupDialogs();
      this._measure();

      var self = this;
      this._onResize = function () {
        clearTimeout(self._resizeTimer);
        self._resizeTimer = setTimeout(function () {
          self._measure();
          self._updateStrip();
        }, 150);
      };
      window.addEventListener('resize', this._onResize);
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function () {
          if (self.isConnected) self._measure();
        });
      }

      /* In the theme editor, selecting a review that sits past Show More brings it into view, and
         a review or a question opens its own tab. */
      this._onBlockSelect = function (event) {
        var row = event.target && event.target.closest && event.target.closest('[data-review-id], [data-question-id]');
        if (!row || !self.contains(row)) return;
        self._tab(row.hasAttribute('data-question-id') ? 'questions' : 'reviews');
        if (row.hidden) row.hidden = false;
      };
      document.addEventListener('shopify:block:select', this._onBlockSelect);

      if (remote() && this.total > this._rows().length && this.moreWrap) this.moreWrap.hidden = false;
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      document.removeEventListener('shopify:block:select', this._onBlockSelect);
    }

    _emit(name, detail) {
      this.dispatchEvent(new CustomEvent('sugar:reviews:' + name, { bubbles: true, detail: detail }));
    }

    _rows() {
      return this.list ? Array.prototype.slice.call(this.list.querySelectorAll(':scope > [data-review-id]')) : [];
    }

    _baseDetail() {
      return {
        sectionId: this.sectionId,
        productId: this.getAttribute('data-product-id') || null,
        productHandle: this.getAttribute('data-product-handle') || null,
      };
    }

    /* ---- clicks ------------------------------------------------------- */

    _onClick(event) {
      var t = event.target.closest('button, a');
      if (!t || !this.contains(t)) return;
      if (t.hasAttribute('data-rv-vote')) return this._vote(t);
      if (t.hasAttribute('data-rv-readmore')) return this._readMore(t);
      if (t.hasAttribute('data-rv-summary-more')) return this._summaryMore(t);
      if (t.hasAttribute('data-rv-bar')) return this._toggleFilter('rating', '', t.getAttribute('data-rv-bar'));
      if (t.hasAttribute('data-rv-chip') || t.hasAttribute('data-rv-active')) {
        return this._toggleFilter(t.getAttribute('data-kind'), t.getAttribute('data-group') || '', t.getAttribute('data-value'));
      }
      if (t.hasAttribute('data-rv-clear-group')) return this._clearGroup(t.getAttribute('data-kind'), t.getAttribute('data-group') || '');
      if (t.hasAttribute('data-rv-clear')) return this._clearFilters();
      if (t.hasAttribute('data-rv-filters-toggle')) return this._togglePanel(t);
      if (t.hasAttribute('data-rv-more')) return this._more();
      if (t.hasAttribute('data-rv-tile')) return this._openLightbox(this._tiles().indexOf(t));
      if (t.hasAttribute('data-rv-photo')) return this._openPhoto(t.getAttribute('data-rv-photo'));
      if (t.hasAttribute('data-rv-full')) return this._openLightbox(-1, t);
      if (t.hasAttribute('data-rv-lb-step')) return this._stepLightbox(parseInt(t.getAttribute('data-rv-lb-step'), 10));
      if (t.hasAttribute('data-rv-strip-nav')) return this._scrollStrip(parseInt(t.getAttribute('data-rv-strip-nav'), 10));
      if (t.hasAttribute('data-rv-write')) return this._openForm(t);
      if (t.hasAttribute('data-rv-tab')) return this._tab(t.getAttribute('data-rv-tab'), true);
      if (t.hasAttribute('data-rv-qmore')) return this._qMore();
      if (t.hasAttribute('data-rv-ask')) return this._openAsk(t);
      if (t.hasAttribute('data-rv-photo-remove')) return this._clearPhoto();
      if (t.hasAttribute('data-rv-close')) {
        var dlg = t.closest('dialog');
        if (dlg) dlg.close();
      }
    }

    _onChange(event) {
      var t = event.target;
      if (t.hasAttribute('data-rv-qsort')) return this._qSort(t.value);
      if (t.hasAttribute('data-rv-sort')) {
        this.sort = t.value;
        this.shown = this.perPage;
        this._emit('sort', Object.assign(this._baseDetail(), { sort: this.sort }));
        if (remote()) this._load('sort');
        else this._apply();
        return;
      }
      if (t.hasAttribute('data-rv-photo-input')) this._previewPhoto(t);
      if (t.name === 'rating') this._fieldError('rating', false);
    }

    _onInput(event) {
      var t = event.target;
      if (t.hasAttribute('data-rv-search')) {
        var self = this;
        clearTimeout(this._searchTimer);
        this._searchTimer = setTimeout(function () {
          self._setQuery(t.value);
        }, 250);
        return;
      }
      if (t.name && t.getAttribute('aria-invalid') === 'true') this._fieldError(t.name, false);
    }

    _onKey(event) {
      var tab = event.target.closest && event.target.closest('[data-rv-tab]');
      if (tab && (event.key === 'ArrowLeft' || event.key === 'ArrowRight' || event.key === 'Home' || event.key === 'End')) {
        event.preventDefault();
        var tabs = Array.prototype.slice.call(this.querySelectorAll('[data-rv-tab]'));
        var i = tabs.indexOf(tab);
        if (event.key === 'ArrowLeft') i = (i - 1 + tabs.length) % tabs.length;
        else if (event.key === 'ArrowRight') i = (i + 1) % tabs.length;
        else if (event.key === 'Home') i = 0;
        else i = tabs.length - 1;
        this._tab(tabs[i].getAttribute('data-rv-tab'), true);
        tabs[i].focus();
        return;
      }
      if (event.key === 'Enter' && event.target.hasAttribute && event.target.hasAttribute('data-rv-search')) {
        event.preventDefault();
        clearTimeout(this._searchTimer);
        this._setQuery(event.target.value);
        return;
      }
      var lb = this.querySelector('[data-rv-lightbox]');
      if (!lb || !lb.open) return;
      if (event.key === 'ArrowLeft') this._stepLightbox(-1);
      else if (event.key === 'ArrowRight') this._stepLightbox(1);
    }

    /* ---- list: filter, sort, paginate --------------------------------- */

    _hasFilters() {
      if (this.filters.rating.length || this.filters.keywords.length || this.filters.query) return true;
      var g = this.filters.groups;
      return Object.keys(g).some(function (k) {
        return g[k].length > 0;
      });
    }

    /* Chips in one group widen the list and groups narrow it; every word of a search must be in
       the review. */
    _matches(row) {
      if (remote()) return true;
      var d = rowData(row);
      var f = this.filters;
      if (f.rating.length && f.rating.indexOf(d.rating) === -1) return false;
      if (f.keywords.length) {
        var found = this.keywords.some(function (k) {
          return (
            f.keywords.indexOf(k.label) !== -1 &&
            k.finds.some(function (find) {
              return find(d.text);
            })
          );
        });
        if (!found) return false;
      }
      if (this._queryFinds) {
        for (var q = 0; q < this._queryFinds.length; q++) {
          if (!this._queryFinds[q](d.text)) return false;
        }
      }
      for (var label in f.groups) {
        var wanted = f.groups[label];
        if (!wanted.length) continue;
        var have = splitValues(d.lookup[norm(label)]);
        var hit = wanted.some(function (v) {
          return have.indexOf(v) !== -1;
        });
        if (!hit) return false;
      }
      return true;
    }

    /* Reads every row first, then writes: reorder only when the order changed, and touch
       `hidden` only on the rows whose visibility does. */
    _apply() {
      var rows = this._rows();
      var order = rows;
      if (!remote()) {
        var cmp = SORTS[this.sort] || SORTS.most_helpful;
        order = rows.slice().sort(function (a, b) {
          return cmp(rowData(a), rowData(b));
        });
      }
      var matches = order.filter(this._matches, this);
      var visible = new Set(matches.slice(0, this.shown));
      var moved = order.some(function (row, i) {
        return rows[i] !== row;
      });

      if (moved) {
        var frag = document.createDocumentFragment();
        order.forEach(function (row) {
          frag.appendChild(row);
        });
        this.list.appendChild(frag);
      }
      order.forEach(function (row) {
        var show = visible.has(row);
        if (row.hidden === show) row.hidden = !show;
      });

      var count = this._hasFilters() && !remote() ? matches.length : this.remoteTotal != null ? this.remoteTotal : this.total;
      if (this.compact) count = String(count).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      if (this.countEl) this.countEl.textContent = this.countLabel.replace('[count]', count);

      var empty = this.querySelector('[data-rv-empty]');
      if (empty) empty.hidden = !(rows.length && matches.length === 0);

      var more = matches.length > this.shown;
      if (!more && remote()) more = this.remoteHasMore !== null ? this.remoteHasMore : this.total > rows.length;
      if (this.moreWrap) this.moreWrap.hidden = !more;
      this._measure();
      return matches;
    }

    _more() {
      var self = this;
      var rows = this._rows();
      var before = rows.filter(function (row) {
        return !row.hidden;
      });
      var matching = rows.filter(this._matches, this).length;
      if (matching > this.shown) {
        this.shown += this.perPage;
        this._apply();
        this._focusFirstNew(before);
        return;
      }
      if (remote()) {
        this._load('more').then(function () {
          self._focusFirstNew(before);
        });
      }
    }

    _focusFirstNew(before) {
      var seen = new Set(before);
      var next = this._rows().filter(function (row) {
        return !row.hidden && !seen.has(row);
      })[0];
      if (!next) return;
      next.setAttribute('tabindex', '-1');
      next.focus({ preventScroll: true });
    }

    /* ---- backend loading (adapter.remote only) ------------------------ */

    _filterState() {
      var groups = {};
      var g = this.filters.groups;
      Object.keys(g).forEach(function (k) {
        if (g[k].length) groups[k] = g[k].slice();
      });
      return { rating: this.filters.rating.slice(), keywords: this.filters.keywords.slice(), query: this.filters.query, groups: groups };
    }

    _load(reason) {
      var self = this;
      var rows = this._rows();
      var params = Object.assign(this._baseDetail(), {
        reason: reason,
        sort: this.sort,
        filters: this._filterState(),
        offset: reason === 'more' ? rows.length : 0,
        limit: this.perPage,
      });
      var button = this.querySelector('[data-rv-more]');
      this.setAttribute('aria-busy', 'true');
      if (button) button.disabled = true;
      return call('load', [params])
        .then(function (result) {
          if (!result || !Array.isArray(result.reviews)) {
            if (reason === 'more') self.remoteHasMore = false;
            return;
          }
          if (reason !== 'more' && result.replace !== false) {
            rows.forEach(function (row) {
              row.remove();
            });
            self.shown = self.perPage;
          }
          var start = self._rows().length;
          result.reviews.forEach(function (review, i) {
            self.list.appendChild(self._buildRow(review, start + i));
          });
          if (typeof result.total === 'number') self.remoteTotal = result.total;
          self.remoteHasMore = result.hasMore === undefined ? result.reviews.length >= self.perPage : !!result.hasMore;
          if (reason === 'more') self.shown += self.perPage;
          self._restoreVotes();
        })
        .catch(function (err) {
          console.error('[sugar-reviews] load failed', err);
        })
        .then(function () {
          self.removeAttribute('aria-busy');
          if (button) button.disabled = false;
          self._apply();
          self._measure();
        });
    }

    /* A review from load(), drawn with the same classes the server uses. Icons and the product
       row are cloned from the section's own <template>, so they match the server rows exactly. */
    _buildRow(r, index) {
      var parts = this.querySelector('template[data-rv-parts]');
      var part = function (name) {
        var el = parts && parts.content.querySelector('[data-part="' + name + '"]');
        return el ? el.innerHTML : '';
      };
      var labels = (parts && parts.content.querySelector('[data-part="labels"]')) || { getAttribute: function () { return ''; } };
      var id = String(r.id != null ? r.id : 'remote-' + index);
      var rating = Math.max(0, Math.min(5, parseFloat(r.rating) || 0));
      var details = r.details && typeof r.details === 'object' ? r.details : {};
      var photos = Array.isArray(r.photos) ? r.photos.slice(0, 2) : [];
      var video = r.video ? (typeof r.video === 'string' ? { src: r.video } : r.video) : null;
      if (video && !video.src) video = null;
      var author = String(r.author || '');
      var initials = author
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(function (w) {
          return w.charAt(0);
        })
        .join('')
        .toUpperCase();
      var date = r.date ? String(r.date) : '';
      var dateLabel = parseDate(date) ? relativeDate(date) : date;
      var body = r.bodyHtml != null ? String(r.bodyHtml) : r.body ? '<p>' + escapeHtml(r.body).replace(/\n+/g, '</p><p>') + '</p>' : '';
      var up = parseInt(r.helpfulUp, 10) || 0;
      var down = parseInt(r.helpfulDown, 10) || 0;
      var helpful = labels.getAttribute('data-helpful');

      var detailHtml = '';
      var keys = Object.keys(details);
      if (keys.length) {
        detailHtml =
          '<dl class="sugar-okendo-reviews__details">' +
          keys
            .map(function (k) {
              var v = String(details[k]);
              return '<div class="sugar-okendo-reviews__detail"><dt>' + escapeHtml(k) + '</dt><dd>' + escapeHtml(v) + '</dd></div>';
            })
            .join('') +
          '</dl>';
      }

      var row = document.createElement('article');
      row.className = 'sugar-okendo-reviews__review';
      row.id = 'Review-' + id;
      row.setAttribute('data-review-id', id);
      row.setAttribute('data-index', String(index));
      row.setAttribute('data-rating', String(Math.round(rating)));
      row.setAttribute('data-date', parseDate(date) ? date : '');
      row.setAttribute('data-helpful-up', String(up));
      row.setAttribute('data-helpful-down', String(down));
      row.setAttribute('data-has-photos', photos.length || video ? 'true' : 'false');
      row.setAttribute('data-details', JSON.stringify(details));
      row.hidden = true;
      row.innerHTML =
        '<div class="sugar-okendo-reviews__author">' +
        '<div class="sugar-okendo-reviews__who"><span class="sugar-okendo-reviews__avatar" aria-hidden="true">' +
        (r.avatar ? '<img class="sugar-okendo-reviews__avatar-img" src="' + escapeHtml(r.avatar) + '" alt="" width="48" height="48" loading="lazy">' : escapeHtml(initials)) +
        '</span><p class="sugar-okendo-reviews__who-text"><span class="sugar-okendo-reviews__name">' + escapeHtml(author) + '</span>' +
        (r.verified ? '<span class="sugar-okendo-reviews__badge">' + escapeHtml(r.verified === true ? 'Verified Buyer' : r.verified) + part('badge') + '</span>' : '') +
        '</p></div>' +
        part('product') +
        (r.recommend
          ? '<p class="sugar-okendo-reviews__recommend"><span class="sugar-okendo-reviews__recommend-icon" aria-hidden="true">' + part('recommend') + '</span>' + escapeHtml(labels.getAttribute('data-recommend')) + '</p>'
          : r.recommend === false && labels.getAttribute('data-not-recommend')
          ? '<p class="sugar-okendo-reviews__recommend sugar-okendo-reviews__recommend--no">' + escapeHtml(labels.getAttribute('data-not-recommend')) + '</p>'
          : '') +
        detailHtml +
        '</div>' +
        '<div class="sugar-okendo-reviews__content">' +
        '<div class="sugar-okendo-reviews__meta"><span class="sugar-stars sugar-okendo-reviews__stars" style="--stars-rating:' + rating + '" role="img" aria-label="' + rating + ' / 5"><span class="sugar-stars__symbol"></span><span class="sugar-stars__fill"></span></span>' +
        (dateLabel ? '<span class="sugar-okendo-reviews__date"' + (parseDate(date) ? ' data-rv-date="' + escapeHtml(date) + '"' : '') + '>' + escapeHtml(dateLabel) + '</span>' : '') +
        '</div>' +
        (r.title ? '<h3 class="sugar-okendo-reviews__title">' + escapeHtml(r.title) + '</h3>' : '') +
        (body
          ? '<div class="sugar-okendo-reviews__body" data-rv-body><div class="sugar-okendo-reviews__text" id="ReviewText-' + escapeHtml(id) + '" data-rv-text>' + body + '</div>' +
            '<button type="button" class="sugar-okendo-reviews__link sugar-okendo-reviews__read-more" data-rv-readmore aria-expanded="false" aria-controls="ReviewText-' + escapeHtml(id) + '">' + escapeHtml(labels.getAttribute('data-read-more')) + '</button></div>'
          : '') +
        (photos.length || video
          ? '<div class="sugar-okendo-reviews__photos">' +
            photos
              .map(function (p) {
                var src = typeof p === 'string' ? p : p.src;
                var full = (p && p.full) || src;
                return '<button type="button" class="sugar-okendo-reviews__photo" data-rv-full="' + escapeHtml(full) + '" aria-label="' + escapeHtml(labels.getAttribute('data-view-photo')) + '"><img class="sugar-okendo-reviews__photo-img" src="' + escapeHtml(src) + '" alt="' + escapeHtml((p && p.alt) || '') + '" loading="lazy"></button>';
              })
              .join('') +
            (video
              ? '<button type="button" class="sugar-okendo-reviews__photo sugar-okendo-reviews__photo--video" data-rv-full="' + escapeHtml(video.poster || '') + '" data-video="' + escapeHtml(video.src) + '" aria-label="' +
                escapeHtml(labels.getAttribute('data-view-photo')) + '">' +
                (video.poster ? '<img class="sugar-okendo-reviews__photo-img" src="' + escapeHtml(video.poster) + '" alt="" loading="lazy">' : '') +
                '<span class="sugar-okendo-reviews__play" aria-hidden="true">' + part('play') + '</span></button>'
              : '') +
            '</div>'
          : '') +
        '<div class="sugar-okendo-reviews__helpful"><span>' + escapeHtml(helpful) + '</span>' +
        '<button type="button" class="sugar-okendo-reviews__vote" data-rv-vote="up" aria-pressed="false"><span class="visually-hidden">' + escapeHtml(helpful) + ' +</span>' + part('up') + '<span data-rv-votes>' + up + '</span></button>' +
        '<button type="button" class="sugar-okendo-reviews__vote" data-rv-vote="down" aria-pressed="false"><span class="visually-hidden">' + escapeHtml(helpful) + ' −</span>' + part('down') + '<span data-rv-votes>' + down + '</span></button>' +
        '</div></div>';
      return row;
    }

    /* ---- filters ------------------------------------------------------ */

    _part(name) {
      var parts = this.querySelector('template[data-rv-parts]');
      var el = parts && parts.content.querySelector('[data-part="' + name + '"]');
      return el ? el.innerHTML : '';
    }

    _label(name, fallback) {
      var parts = this.querySelector('template[data-rv-parts]');
      var el = parts && parts.content.querySelector('[data-part="labels"]');
      return (el && el.getAttribute('data-' + name)) || fallback;
    }

    _panelOpen() {
      var panel = this.querySelector('[data-rv-panel]');
      return !!(panel && !panel.hidden);
    }

    _togglePanel(button) {
      var panel = this.querySelector('[data-rv-panel]');
      if (!panel) return;
      var open = panel.hidden;
      if (open) this._buildPanel(panel);
      panel.hidden = !open;
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      this._renderActive();
    }

    _values(label) {
      var key = norm(label);
      var seen = {};
      var out = [];
      this._rows().forEach(function (row) {
        splitValues(rowData(row).lookup[key]).forEach(function (v) {
          if (!seen[v]) {
            seen[v] = true;
            out.push(v);
          }
        });
      });
      return out.sort(function (a, b) {
        return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
      });
    }

    _chip(kind, group, value, inner, label) {
      var on = this._isOn(kind, group, value);
      return (
        '<button type="button" class="sugar-okendo-reviews__chip' + (kind === 'rating' ? ' sugar-okendo-reviews__chip--stars' : '') + '" data-rv-chip data-kind="' + kind + '" data-group="' + escapeHtml(group) +
        '" data-value="' + escapeHtml(value) + '" aria-pressed="' + on + '"' + (label ? ' aria-label="' + escapeHtml(label) + '"' : '') + '>' + inner + '</button>'
      );
    }

    /* A group's title carries its own Clear, shown while any of its chips is on. */
    _group(kind, group, title, chips) {
      var clear = this._label('clear', 'Clear');
      return (
        '<div class="sugar-okendo-reviews__filter-group"><p class="sugar-okendo-reviews__filter-title">' + escapeHtml(title) +
        '<button type="button" class="sugar-okendo-reviews__filter-clear" data-rv-clear-group data-kind="' + kind + '" data-group="' + escapeHtml(group) + '"' +
        (this._groupOn(kind, group) ? '' : ' hidden') + '>' + this._part('clear') + escapeHtml(clear) +
        '<span class="visually-hidden"> ' + escapeHtml(title) + '</span></button></p>' +
        '<div class="sugar-okendo-reviews__chips" role="group" aria-label="' + escapeHtml(title) + '">' + chips + '</div></div>'
      );
    }

    /* Search, Keywords and Rating, then one group for each Filter by details label: its own chips
       in the merchant's order, or the values the reviews use. */
    _buildPanel(panel) {
      var self = this;
      var html = '';
      if (this.search) {
        var id = 'ReviewsSearch-' + this.sectionId;
        html +=
          '<div class="sugar-okendo-reviews__filter-group"><label class="sugar-okendo-reviews__filter-title" for="' + escapeHtml(id) + '">' + escapeHtml(this._label('search', 'Search')) + '</label>' +
          '<span class="sugar-okendo-reviews__search">' + this._part('search') +
          '<input class="sugar-okendo-reviews__search-input" type="search" id="' + escapeHtml(id) + '" data-rv-search placeholder="Search Reviews" maxlength="80" autocomplete="off" enterkeyhint="search" value="' +
          escapeHtml(this.filters.query) + '"></span></div>';
      }
      if (this.keywords.length) {
        html += this._group(
          'keyword',
          '',
          'Keywords',
          this.keywords
            .map(function (k) {
              return self._chip('keyword', '', k.label, escapeHtml(k.label));
            })
            .join('')
        );
      }
      html += this._group(
        'rating',
        '',
        'Rating',
        [1, 2, 3, 4, 5]
          .map(function (n) {
            return self._chip('rating', '', String(n), starsMarkup(n), n + ' ★');
          })
          .join('')
      );
      this.groups.forEach(function (g) {
        var values = g.values && g.values.length ? g.values : self._values(g.label);
        if (!values.length) return;
        html += self._group(
          'detail',
          g.label,
          g.label,
          values
            .map(function (v) {
              return self._chip('detail', g.label, v, escapeHtml(v));
            })
            .join('')
        );
      });
      panel.innerHTML = html;
    }

    _list(kind, group) {
      if (kind === 'rating') return this.filters.rating;
      if (kind === 'keyword') return this.filters.keywords;
      return this.filters.groups[group] || (this.filters.groups[group] = []);
    }

    _isOn(kind, group, value) {
      if (kind === 'rating') return this.filters.rating.indexOf(parseInt(value, 10)) !== -1;
      if (kind === 'keyword') return this.filters.keywords.indexOf(value) !== -1;
      return (this.filters.groups[group] || []).indexOf(value) !== -1;
    }

    _groupOn(kind, group) {
      if (kind === 'rating') return this.filters.rating.length > 0;
      if (kind === 'keyword') return this.filters.keywords.length > 0;
      return (this.filters.groups[group] || []).length > 0;
    }

    _toggleFilter(kind, group, value) {
      if (kind === 'query') return this._setQuery('');
      var list = this._list(kind, group);
      var v = kind === 'rating' ? parseInt(value, 10) : value;
      var i = list.indexOf(v);
      if (i === -1) list.push(v);
      else list.splice(i, 1);
      this._filtersChanged();
    }

    _clearGroup(kind, group) {
      this._list(kind, group).length = 0;
      this._filtersChanged();
    }

    _setQuery(value) {
      var query = String(value || '').trim();
      var input = this.querySelector('[data-rv-search]');
      if (input && input.value.trim() !== query) input.value = query;
      if (query === this.filters.query) return;
      this.filters.query = query;
      this._queryFinds = query ? norm(query).split(/\s+/).map(wordFinder) : null;
      this._filtersChanged();
    }

    _clearFilters() {
      this.filters = { rating: [], keywords: [], groups: {}, query: '' };
      this._queryFinds = null;
      var input = this.querySelector('[data-rv-search]');
      if (input) input.value = '';
      this._filtersChanged();
    }

    _filtersChanged() {
      var self = this;
      this.shown = this.perPage;
      var matches = remote() ? null : this._apply();

      this.querySelectorAll('[data-rv-bar]').forEach(function (bar) {
        var on = self._isOn('rating', '', bar.getAttribute('data-rv-bar'));
        if ((bar.getAttribute('aria-pressed') === 'true') !== on) bar.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      this.querySelectorAll('[data-rv-chip]').forEach(function (chip) {
        var on = self._isOn(chip.getAttribute('data-kind'), chip.getAttribute('data-group'), chip.getAttribute('data-value'));
        if ((chip.getAttribute('aria-pressed') === 'true') !== on) chip.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      this.querySelectorAll('[data-rv-clear-group]').forEach(function (btn) {
        var hide = !self._groupOn(btn.getAttribute('data-kind'), btn.getAttribute('data-group'));
        if (btn.hidden !== hide) btn.hidden = hide;
      });
      this._renderActive();

      this._emit(
        'filter',
        Object.assign(this._baseDetail(), {
          filters: this._filterState(),
          count: matches ? matches.length : null,
        })
      );
      if (remote()) this._load('filter');
    }

    /* The chosen filters above the list, while the panel that holds them is closed (a bar picks a
       rating without opening it). */
    _renderActive() {
      var box = this.querySelector('[data-rv-active-list]');
      if (!box) return;
      var html = '';
      var chip = function (kind, group, value, text, label) {
        return (
          '<button type="button" class="sugar-okendo-reviews__active-chip" data-rv-active data-kind="' + kind + '" data-group="' + escapeHtml(group) + '" data-value="' + escapeHtml(value) + '"' +
          (label ? ' aria-label="' + escapeHtml(label) + '"' : '') + '>' + text + '<span aria-hidden="true">×</span></button>'
        );
      };
      if (this.filters.query) html += chip('query', '', '', '“' + escapeHtml(this.filters.query) + '”');
      this.filters.keywords.forEach(function (k) {
        html += chip('keyword', '', k, escapeHtml(k));
      });
      this.filters.rating
        .slice()
        .sort(function (a, b) {
          return b - a;
        })
        .forEach(function (n) {
          html += chip('rating', '', String(n), n + '<span class="sugar-okendo-reviews__star" aria-hidden="true"></span>', n + ' ★ ×');
        });
      var g = this.filters.groups;
      Object.keys(g).forEach(function (label) {
        g[label].forEach(function (v) {
          html += chip('detail', label, v, escapeHtml(label) + ': ' + escapeHtml(v));
        });
      });
      if (html) html += '<button type="button" class="sugar-okendo-reviews__link" data-rv-clear>Clear all</button>';
      var hide = !html || this._panelOpen();
      if (box.innerHTML !== html) box.innerHTML = html;
      if (box.hidden !== hide) box.hidden = hide;
    }

    /* ---- helpful votes ------------------------------------------------ */

    _voteKey(row) {
      return 'sugar-reviews:' + this.sectionId + ':' + row.getAttribute('data-review-id');
    }

    _restoreVotes() {
      var self = this;
      this._rows().forEach(function (row) {
        if (row._voteRead) return;
        row._voteRead = true;
        var saved = storeGet(self._voteKey(row));
        if (saved === 'up' || saved === 'down') self._paintVote(row, saved);
      });
    }

    _paintVote(row, vote) {
      var d = rowData(row);
      row._vote = vote || null;
      row.querySelectorAll('[data-rv-vote]').forEach(function (btn) {
        var dir = btn.getAttribute('data-rv-vote');
        var base = dir === 'up' ? d.up : d.down;
        btn.setAttribute('aria-pressed', vote === dir ? 'true' : 'false');
        var n = btn.querySelector('[data-rv-votes]');
        if (n) n.textContent = String(base + (vote === dir ? 1 : 0));
      });
    }

    _vote(button) {
      var row = button.closest('[data-review-id]');
      if (!row) return;
      var self = this;
      var dir = button.getAttribute('data-rv-vote');
      var previous = row._vote || null;
      var vote = previous === dir ? null : dir;
      var d = rowData(row);
      this._paintVote(row, vote);
      storeSet(this._voteKey(row), vote);
      var detail = Object.assign(this._baseDetail(), {
        reviewId: d.id,
        vote: vote,
        previous: previous,
        helpfulUp: d.up + (vote === 'up' ? 1 : 0),
        helpfulDown: d.down + (vote === 'down' ? 1 : 0),
      });
      this._emit('vote', detail);
      call('vote', [detail]).then(
        function (res) {
          if (res && res.ok === false) throw new Error(res.message || 'Vote rejected');
        }
      ).catch(function (err) {
        console.error('[sugar-reviews] vote failed', err);
        self._paintVote(row, previous);
        storeSet(self._voteKey(row), previous);
      });
    }

    /* ---- Read More ---------------------------------------------------- */

    /* A Read More the layout clipped away is also taken out of the tab order. Reads every body on
       show, then writes; a review still hidden past Show More is measured when it appears. */
    _measure() {
      var checks = [];
      this.querySelectorAll('[data-rv-body]:not(.is-open)').forEach(function (body) {
        var text = body.querySelector('[data-rv-text]');
        var btn = body.querySelector('[data-rv-readmore]');
        if (!text || !btn || body.closest('[hidden]')) return;
        var lh = parseFloat(getComputedStyle(text).lineHeight) || 0;
        checks.push([btn, text.offsetHeight > lh * 4 + 1]);
      });
      var summary = this.querySelector('[data-rv-summary]');
      var summaryBtn = this.querySelector('[data-rv-summary-more]');
      var summaryNeeds = null;
      if (summary && summaryBtn && !summary.classList.contains('is-open')) {
        var clamped = getComputedStyle(summary).webkitLineClamp;
        summaryNeeds = clamped && clamped !== 'none' ? summary.scrollHeight > summary.clientHeight + 1 : true;
      }
      checks.forEach(function (c) {
        if (c[0].hidden === c[1]) c[0].hidden = !c[1];
      });
      if (summaryNeeds !== null && summaryBtn.hidden === summaryNeeds) summaryBtn.hidden = !summaryNeeds;
    }

    _readMore(button) {
      var body = button.closest('[data-rv-body]');
      if (!body) return;
      body.classList.add('is-open');
      button.setAttribute('aria-expanded', 'true');
      button.hidden = true;
      var text = body.querySelector('[data-rv-text]');
      if (text) {
        text.setAttribute('tabindex', '-1');
        text.focus({ preventScroll: true });
      }
    }

    _summaryMore(button) {
      var summary = this.querySelector('[data-rv-summary]');
      if (!summary) return;
      summary.classList.add('is-open');
      button.setAttribute('aria-expanded', 'true');
      button.hidden = true;
      summary.setAttribute('tabindex', '-1');
      summary.focus({ preventScroll: true });
    }

    _refreshDates() {
      var writes = [];
      this.querySelectorAll('[data-rv-date]').forEach(function (el) {
        var label = relativeDate(el.getAttribute('data-rv-date'));
        if (label && el.textContent !== label) writes.push([el, label]);
      });
      writes.forEach(function (w) {
        w[0].textContent = w[1];
      });
    }

    /* ---- photo strip -------------------------------------------------- */

    _setupStrip() {
      var strip = this.querySelector('[data-rv-strip]');
      if (!strip) return;
      this.strip = strip;
      this.track = strip.querySelector('[data-rv-track]');
      var self = this;
      var queued = false;
      this.track.addEventListener(
        'scroll',
        function () {
          if (queued) return;
          queued = true;
          requestAnimationFrame(function () {
            queued = false;
            self._updateStrip();
          });
        },
        { passive: true }
      );
      this._updateStrip();
    }

    _updateStrip() {
      if (!this.track) return;
      var max = this.track.scrollWidth - this.track.clientWidth;
      var x = Math.abs(this.track.scrollLeft);
      this.strip.toggleAttribute('data-at-start', x <= 1);
      this.strip.toggleAttribute('data-at-end', x >= max - 1);
    }

    _scrollStrip(dir) {
      if (!this.track) return;
      var step = Math.max(124, Math.floor(this.track.clientWidth / 124) * 124 - 124);
      this.track.scrollBy({ left: dir * step, behavior: 'smooth' });
    }

    _tiles() {
      return Array.prototype.slice.call(this.querySelectorAll('[data-rv-tile]'));
    }

    /* ---- dialogs ------------------------------------------------------ */

    _setupDialogs() {
      var self = this;
      this.querySelectorAll('dialog').forEach(function (dlg) {
        dlg.addEventListener('click', function (event) {
          if (event.target === dlg) dlg.close();
        });
        dlg.addEventListener('close', function () {
          var video = dlg.querySelector('video');
          if (video) video.pause();
          if (self._opener && self._opener.isConnected) self._opener.focus({ preventScroll: true });
          self._opener = null;
        });
      });
    }

    _openPhoto(key) {
      var tiles = this._tiles();
      for (var i = 0; i < tiles.length; i++) {
        if (tiles[i].getAttribute('data-rv-key') === key) return this._openLightbox(i);
      }
    }

    /* index is a strip tile; a photo of a review loaded by a backend has no tile, so it is passed
       as `source` and opens on its own, without stepping. */
    _openLightbox(index, source) {
      var dlg = this.querySelector('[data-rv-lightbox]');
      var tiles = this._tiles();
      var tile = source || tiles[index];
      if (!dlg || !tile) return;
      if (!dlg.open) this._opener = document.activeElement;
      this._lbIndex = index;

      var img = dlg.querySelector('[data-rv-lb-img]');
      var placeholder = dlg.querySelector('.sugar-okendo-reviews__lightbox-placeholder');
      var thumb = tile.querySelector('img');
      var full = tile.getAttribute('data-full') || tile.getAttribute('data-rv-full');
      var video = dlg.querySelector('[data-rv-lb-video]');
      var videoUrl = video ? tile.getAttribute('data-video') : null;
      if (video && !videoUrl && !video.hidden) {
        video.pause();
        video.removeAttribute('src');
        video.load();
        video.hidden = true;
      }
      if (videoUrl) {
        if (video.getAttribute('src') !== videoUrl) video.src = videoUrl;
        if (full) video.poster = full;
        else video.removeAttribute('poster');
        video.hidden = false;
        img.removeAttribute('src');
        img.hidden = true;
        if (placeholder) placeholder.style.display = 'none';
      } else if (full) {
        img.src = full;
        img.alt = thumb ? thumb.alt : '';
        img.hidden = false;
        if (placeholder) placeholder.style.display = 'none';
      } else {
        img.removeAttribute('src');
        img.hidden = true;
        if (placeholder) placeholder.style.display = '';
      }

      var panel = dlg.querySelector('[data-rv-lb-review]');
      var id = tile.getAttribute('data-review-id') || (source && source.closest('[data-review-id]') ? source.closest('[data-review-id]').getAttribute('data-review-id') : null);
      var row = id && this.list && this.list.querySelector(':scope > [data-review-id="' + (window.CSS && CSS.escape ? CSS.escape(id) : id) + '"]');
      if (panel) {
        if (row) {
          var stars = row.querySelector('.sugar-okendo-reviews__meta');
          var title = row.querySelector('.sugar-okendo-reviews__title');
          var text = row.querySelector('[data-rv-text]');
          var name = row.querySelector('.sugar-okendo-reviews__name');
          var badge = row.querySelector('.sugar-okendo-reviews__badge');
          panel.innerHTML =
            (stars ? stars.outerHTML : '') +
            (title ? title.outerHTML : '') +
            '<p class="sugar-okendo-reviews__lightbox-author"><span class="sugar-okendo-reviews__name">' + (name ? name.innerHTML : '') + '</span>' + (badge ? ' ' + badge.outerHTML : '') + '</p>' +
            (text ? '<div class="sugar-okendo-reviews__text">' + text.innerHTML + '</div>' : '');
          panel.hidden = false;
        } else {
          panel.innerHTML = '';
          panel.hidden = true;
        }
      }

      var prev = dlg.querySelector('[data-rv-lb-step="-1"]');
      var next = dlg.querySelector('[data-rv-lb-step="1"]');
      if (prev) prev.disabled = !!source || index <= 0;
      if (next) next.disabled = !!source || index >= tiles.length - 1;
      if (!dlg.open) dlg.showModal();
      if (videoUrl) {
        var playing = video.play();
        if (playing && playing.catch) playing.catch(function () {});
      }
    }

    _stepLightbox(dir) {
      var tiles = this._tiles();
      var i = (this._lbIndex || 0) + dir;
      if (i < 0 || i >= tiles.length) return;
      this._openLightbox(i);
    }

    _openForm(button) {
      var dlg = this.querySelector('[data-rv-form-dialog]');
      if (!dlg) return;
      var form = dlg.querySelector('[data-rv-form]');
      var thanks = dlg.querySelector('[data-rv-thanks]');
      if (thanks && !thanks.hidden) {
        form.reset();
        this._clearPhoto();
        thanks.hidden = true;
        form.hidden = false;
      }
      this._opener = button;
      dlg.showModal();
      var first = form.querySelector('input[name="rating"]');
      if (first) first.focus({ preventScroll: true });
    }

    _previewPhoto(input) {
      var box = this.querySelector('[data-rv-preview]');
      var img = this.querySelector('[data-rv-preview-img]');
      var file = input.files && input.files[0];
      if (this._photoUrl) URL.revokeObjectURL(this._photoUrl);
      this._photoUrl = null;
      if (!file || !box || !img) {
        if (box) box.hidden = true;
        return;
      }
      this._photoUrl = URL.createObjectURL(file);
      img.src = this._photoUrl;
      box.hidden = false;
    }

    _clearPhoto() {
      var input = this.querySelector('[data-rv-photo-input]');
      if (input) input.value = '';
      var box = this.querySelector('[data-rv-preview]');
      if (box) box.hidden = true;
      if (this._photoUrl) URL.revokeObjectURL(this._photoUrl);
      this._photoUrl = null;
    }

    _fieldError(name, on) {
      var err = this.querySelector('[data-rv-error="' + name + '"]');
      if (err) err.hidden = !on;
      this.querySelectorAll('[data-rv-form] [name="' + name + '"]').forEach(function (input) {
        if (on) input.setAttribute('aria-invalid', 'true');
        else input.removeAttribute('aria-invalid');
      });
    }

    /* ---- questions ------------------------------------------------------ */

    _tab(name, user) {
      var tabs = this.querySelectorAll('[data-rv-tab]');
      if (!tabs.length) return;
      tabs.forEach(function (tab) {
        var on = tab.getAttribute('data-rv-tab') === name;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
      });
      this.querySelectorAll('[data-rv-tabpanel]').forEach(function (panel) {
        panel.hidden = panel.getAttribute('data-rv-tabpanel') !== name;
      });
      /* Read More is measured against the text's box, which a hidden panel does not have. */
      if (name === 'reviews') this._measure();
      if (user) this._emit('tab', Object.assign(this._baseDetail(), { tab: name }));
    }

    _qRows() {
      var list = this.querySelector('[data-rv-qlist]');
      return list ? Array.prototype.slice.call(list.querySelectorAll(':scope > [data-question-id]')) : [];
    }

    _qShow() {
      var shown = this.qShown || this.perPage;
      var rows = this._qRows();
      rows.forEach(function (row, i) {
        row.hidden = i >= shown;
      });
      var wrap = this.querySelector('[data-rv-qmore-wrap]');
      if (wrap) wrap.hidden = rows.length <= shown;
    }

    _qMore() {
      var rows = this._qRows();
      var before = this.qShown || this.perPage;
      this.qShown = before + this.perPage;
      this._qShow();
      if (rows[before]) rows[before].setAttribute('tabindex', '-1');
      if (rows[before]) rows[before].focus({ preventScroll: true });
    }

    /* The blocks are entered newest first, so Most Recent is their order and Oldest reverses it. */
    _qSort(by) {
      var list = this.querySelector('[data-rv-qlist]');
      if (!list) return;
      var rows = this._qRows();
      rows.sort(function (a, b) {
        var ia = parseInt(a.getAttribute('data-index'), 10) || 0;
        var ib = parseInt(b.getAttribute('data-index'), 10) || 0;
        return by === 'oldest' ? ib - ia : ia - ib;
      });
      rows.forEach(function (row) {
        list.appendChild(row);
      });
      this.qShown = this.perPage;
      this._qShow();
      this._emit('question-sort', Object.assign(this._baseDetail(), { sort: by }));
    }

    _openAsk(button) {
      var dlg = this.querySelector('[data-rv-ask-dialog]');
      if (!dlg) return;
      var form = dlg.querySelector('[data-rv-ask-form]');
      var thanks = dlg.querySelector('[data-rv-ask-thanks]');
      if (thanks && !thanks.hidden) {
        form.reset();
        thanks.hidden = true;
        form.hidden = false;
      }
      this._opener = button;
      dlg.showModal();
      var first = form.querySelector('[name="question"]');
      if (first) first.focus({ preventScroll: true });
    }

    _submitAsk(form) {
      if (form.getAttribute('aria-busy') === 'true') return;
      var data = new FormData(form);
      var question = {
        question: String(data.get('question') || '').trim(),
        name: String(data.get('name') || '').trim(),
        email: String(data.get('email') || '').trim(),
      };
      var invalid = {
        question: !question.question,
        name: !question.name,
        email: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(question.email),
      };
      var firstBad = null;
      Object.keys(invalid).forEach(function (name) {
        var err = form.querySelector('[data-rv-ask-error="' + name + '"]');
        if (err) err.hidden = !invalid[name];
        var input = form.querySelector('[name="' + name + '"]');
        if (input) {
          if (invalid[name]) input.setAttribute('aria-invalid', 'true');
          else input.removeAttribute('aria-invalid');
        }
        if (invalid[name] && !firstBad) firstBad = input;
      });
      var formError = form.querySelector('[data-rv-ask-form-error]');
      if (formError) formError.hidden = true;
      if (firstBad) {
        firstBad.focus();
        return;
      }

      var detail = Object.assign(this._baseDetail(), { question: question });
      this._emit('ask', detail);
      form.setAttribute('aria-busy', 'true');
      var submit = form.querySelector('[data-rv-ask-submit]');
      if (submit) submit.disabled = true;

      call('ask', [data, detail])
        .then(function (res) {
          if (res && res.ok === false) throw new Error(res.message || '');
          var thanks = form.parentNode.querySelector('[data-rv-ask-thanks]');
          form.hidden = true;
          if (thanks) {
            thanks.hidden = false;
            thanks.focus({ preventScroll: true });
          }
        })
        .catch(function (err) {
          if (formError) {
            formError.textContent = (err && err.message) || 'Something went wrong. Please try again.';
            formError.hidden = false;
          }
        })
        .then(function () {
          form.removeAttribute('aria-busy');
          if (submit) submit.disabled = false;
        });
    }

    _onSubmit(event) {
      var form = event.target;
      if (form.hasAttribute('data-rv-ask-form')) {
        event.preventDefault();
        this._submitAsk(form);
        return;
      }
      if (!form.hasAttribute('data-rv-form')) return;
      event.preventDefault();
      if (form.getAttribute('aria-busy') === 'true') return;
      var self = this;

      var data = new FormData(form);
      var review = {
        rating: parseInt(data.get('rating'), 10) || 0,
        title: String(data.get('title') || '').trim(),
        body: String(data.get('body') || '').trim(),
        name: String(data.get('name') || '').trim(),
        email: String(data.get('email') || '').trim(),
        photo: null,
      };
      var photo = data.get('photo');
      if (photo && photo.size) review.photo = photo;

      var invalid = {
        rating: !review.rating,
        title: !review.title,
        body: !review.body,
        name: !review.name,
        email: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(review.email),
      };
      var firstBad = null;
      Object.keys(invalid).forEach(function (name) {
        self._fieldError(name, invalid[name]);
        if (invalid[name] && !firstBad) firstBad = name;
      });
      var formError = form.querySelector('[data-rv-form-error]');
      if (formError) formError.hidden = true;
      if (firstBad) {
        var target = form.querySelector('[name="' + firstBad + '"]');
        if (target) target.focus();
        return;
      }

      var detail = Object.assign(this._baseDetail(), { review: review });
      this._emit('submit', detail);
      form.setAttribute('aria-busy', 'true');
      var submit = form.querySelector('[data-rv-submit]');
      if (submit) submit.disabled = true;

      call('submit', [data, detail])
        .then(function (res) {
          if (res && res.ok === false) throw new Error(res.message || '');
          var thanks = self.querySelector('[data-rv-thanks]');
          form.hidden = true;
          if (thanks) {
            thanks.hidden = false;
            thanks.focus({ preventScroll: true });
          }
        })
        .catch(function (err) {
          if (formError) {
            formError.textContent = (err && err.message) || 'Something went wrong. Please try again.';
            formError.hidden = false;
          }
        })
        .then(function () {
          form.removeAttribute('aria-busy');
          if (submit) submit.disabled = false;
        });
    }
  }

  customElements.define('sugar-reviews', SugarReviews);
})();
